/// <amd-module name="TFS/Admin/Views/GitHubConnection/Components/GitHubConnectionInitializer" />
import * as React from "react";
import { connect } from "react-redux";
import { VssComponent, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ObservableValue, IObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { Dialog } from "VSSUI/Dialog";

import { IGitHubConnectionState } from "TFS/Admin/Views/GitHubConnection/Contracts";
import { GitHubConnectionActions } from "TFS/Admin/Views/GitHubConnection/redux/GitHubConnectionActions";
import {
    getCanEdit,
    getCollectionId,
    getToggleState,
    getLoading,
    getIsAadConnected,
    getGitHubConnectUrl,
    getIsGitHubConnected,
    getIsToggleEnabled,
    getOrgName
} from "TFS/Admin/Views/GitHubConnection/redux/GitHubConnectionSelectors";

import GitHubConnectResources = require("TFS/Admin/Views/GitHubConnection/Resources");
import { Toggle } from "VSSUI/Toggle";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { TelemetryProperties } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { inviteToggleFeatureFlag } from "TFS/Admin/Views/GitHubConnection/Constants";

interface IGitHubConnectionContainerProps {
    context: IVssComponentContext;
}

interface IGitHubConnectionInitializerOwnProps extends IGitHubConnectionContainerProps {
    canLink: boolean;
    collectionMarkToggle: boolean;
    collectionId: string;
    loading: boolean;
    isAadConnected: boolean;
    gitHubConnectUrl: string;
    isGitHubConnected?: boolean;
    isToggleEnabled?: boolean;
    orgName: string;
}

export type IGitHubConnectionInitializerProps = IGitHubConnectionInitializerOwnProps & typeof ActionsToDispatch;

export class GitHubConnectionInitializer extends VssComponent<IGitHubConnectionInitializerProps, IGitHubConnectionState> {
    private isDisconnectDialogOpen = new ObservableValue<boolean>(false);

    public constructor(props: IGitHubConnectionInitializerProps) {
        super(props);
        const isToggleEnabled: boolean = isFeatureFlagEnabled(this.props.context.pageContext, inviteToggleFeatureFlag, true);
        this.props.initView(isToggleEnabled);
    }

    public render(): JSX.Element {
        return (
            <div>
                <div className="left-margin">
                    <h2 className="fontSizeLL fontWeightHeavy title-l h2-bottom">{GitHubConnectResources.GitHubConnectTitle}</h2>
                    <h2 className="fontSizeL fontWeightSemiBold title-l connection-title">
                        {this.props.isGitHubConnected
                            ? GitHubConnectResources.GitHubDisconnectSubtitle
                            : GitHubConnectResources.GitHubConnectSubtitle}
                    </h2>
                    <div className="top-margin">
                        <div className="fontSizeM overview-heading">
                            {this.props.isGitHubConnected ? GitHubConnectResources.GitHubDisconnectBody : GitHubConnectResources.GitHubConnectBody}
                        </div>
                    </div>
                    {this.props.isToggleEnabled && !this.props.loading && this.props.isGitHubConnected ? (
                        <div>
                            <h2 className="fontSizeL fontWeightSemiBold title-l h2-bottom">{GitHubConnectResources.GitHubOrganizationSubtitle}</h2>
                            <div className="top-margin">
                                <FormatComponent format={GitHubConnectResources.GitHubConnectInviteToggleLabel} />
                                <Toggle
                                    className="github-toggle"
                                    ariaLabel={GitHubConnectResources.Toggle}
                                    checked={this.props.collectionMarkToggle}
                                    onChange={(e, checked) => this._onToggleChange(e, checked)}
                                    disabled={!this.props.isAadConnected}
                                />
                                {this._getToggleText()}
                            </div>
                            <div className="github-divider" />
                        </div>
                    ) : null}
                    <div>
                        <h2 className="fontSizeL fontWeightSemiBold title-l connection-title">{GitHubConnectResources.GitHubDisconnectTtitle}</h2>
                        {!this.props.loading ? (
                            <>
                                <Button
                                    className="top-margin"
                                    text={this._getConnectButtonLabel()}
                                    onClick={
                                        this.props.isGitHubConnected
                                            ? () => {
                                                  this.isDisconnectDialogOpen.value = true;
                                              }
                                            : this._onClickGitHubConnect
                                    }
                                    disabled={this.props && !this.props.canLink}
                                    primary
                                />
                                <div className="github-divider" />
                            </>
                        ) : null}
                    </div>
                </div>
                <Observer isDisconnectDialogOpen={this.isDisconnectDialogOpen}>
                    {(props: { isDisconnectDialogOpen: boolean }) => {
                        return props.isDisconnectDialogOpen ? (
                            <Dialog
                                titleProps={{ text: GitHubConnectResources.DisconnectDialogTitle }}
                                footerButtonProps={[
                                    {
                                        text: GitHubConnectResources.GitHubDisconnectCancelButtonLabel,
                                        onClick: () => {
                                            this.isDisconnectDialogOpen.value = false;
                                        }
                                    },
                                    { className: "delete-button", text: GitHubConnectResources.GitHubDisconnectButtonLabel }
                                ]}
                                onDismiss={() => {
                                    this.isDisconnectDialogOpen.value = false;
                                }}
                            >
                                <div>
                                    {GitHubConnectResources.DisconnectDialogPrefixBody}
                                    <b>{this.props.orgName}</b>
                                    {GitHubConnectResources.DisconnectDialogSuffixBody}
                                </div>
                                <br />
                                <div>{GitHubConnectResources.DisconnectDialogTermsBody}</div>
                            </Dialog>
                        ) : null;
                    }}
                </Observer>
            </div>
        );
    }

    private _onToggleChange = async (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>, checked: boolean) => {
        const toggleToSet: boolean = !this.props.collectionMarkToggle;

        if (toggleToSet) {
            this._sendTelemetry("GitHub Invite Toggle Set");
        } else {
            this._sendTelemetry("GitHub Invite Toggle Unset");
        }

        this.props.markCollection(toggleToSet, this.props.collectionId);
    };

    private _onClickGitHubConnect = () => {
        this._sendTelemetry("Click GitHub Connect Button");

        if (this.props && this.props.gitHubConnectUrl) {
            window.location.href = this.props.gitHubConnectUrl;
        } else {
            window.location.href = GitHubConnectResources.GitHubConnectorUrl;
        }
    };

    private _getConnectButtonLabel = (): string => {
        if (this.props && this.props.isGitHubConnected) {
            return GitHubConnectResources.GitHubDisconnectButtonLabel;
        }

        return GitHubConnectResources.GitHubConnectButtonLabel;
    };

    private _sendTelemetry = (action: string, errorMessage?: string): void => {
        const properties: { [key: string]: any } = {};
        if (errorMessage) {
            properties[TelemetryProperties.ErrorMessage] = errorMessage;
        }

        this.props.logTelemetry(action, properties);
    };

    private _getToggleText = (): string => {
        if (!this.props || !this.props.collectionMarkToggle) {
            return GitHubConnectResources.Off;
        }
        return GitHubConnectResources.On;
    };
}

//Update component props whenever the store's state changes
function mapStateToProps(
    state: IGitHubConnectionState,
    providedProps: IGitHubConnectionContainerProps
): Partial<IGitHubConnectionInitializerOwnProps> {
    return {
        ...providedProps,
        canLink: getCanEdit(state),
        collectionId: getCollectionId(state),
        collectionMarkToggle: getToggleState(state),
        loading: getLoading(state),
        isAadConnected: getIsAadConnected(state),
        gitHubConnectUrl: getGitHubConnectUrl(state),
        isGitHubConnected: getIsGitHubConnected(state),
        isToggleEnabled: getIsToggleEnabled(state),
        orgName: getOrgName(state)
    };
}

//Hook up action creators to reducer
const ActionsToDispatch = {
    initView: GitHubConnectionActions.initView,
    markCollection: GitHubConnectionActions.markCollection,
    logTelemetry: GitHubConnectionActions.logTelemetry
};

export const ConnectedGitHubConnectionInitializer = connect(
    mapStateToProps,
    ActionsToDispatch
)(GitHubConnectionInitializer);
